import React, { useState, useEffect } from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { decimalToHoursMinutes } from '../../utils/timeIntervals';

/**
 * TimeIntervalInput Component
 * Separate inputs for hours (whole number) and minutes (5-min intervals)
 * Combines them into decimal hours for storage
 *
 * @param {string} label - Label for the input group
 * @param {number} value - Current value in decimal hours (e.g., 1.5)
 * @param {function} onChange - Callback function when value changes, receives decimal hours
 * @param {boolean} disabled - Whether the fields are disabled
 * @param {string} helperText - Helper text to display below the fields
 * @param {object} sx - Additional MUI sx styling
 */
const TimeIntervalInput = ({
  label,
  value,
  onChange,
  disabled = false,
  helperText = '',
  sx = {}
}) => {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);

  // Convert decimal value to hours and minutes when value prop changes
  useEffect(() => {
    const { hours: h, minutes: m } = decimalToHoursMinutes(value || 0);
    setHours(h);
    setMinutes(m);
  }, [value]);

  const handleHoursChange = (e) => {
    const newHours = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
    setHours(newHours);

    // Calculate decimal and notify parent
    const decimalValue = newHours + (minutes / 60);
    onChange(decimalValue);
  };

  const handleMinutesChange = (e) => {
    const newMinutes = parseInt(e.target.value);
    setMinutes(newMinutes);

    // Calculate decimal and notify parent
    const decimalValue = hours + (newMinutes / 60);
    onChange(decimalValue);
  };

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          label={`${label} (Hours)`}
          type="number"
          value={hours === 0 ? '' : hours}
          onChange={handleHoursChange}
          disabled={disabled}
          inputProps={{ min: 0, step: 1 }}
          sx={{ flex: 2 }}
        />

        <FormControl sx={{ flex: 1 }} disabled={disabled}>
          <InputLabel>Minutes</InputLabel>
          <Select
            value={minutes}
            onChange={handleMinutesChange}
            label="Minutes"
          >
            <MenuItem value={0}>0</MenuItem>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={35}>35</MenuItem>
            <MenuItem value={40}>40</MenuItem>
            <MenuItem value={45}>45</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={55}>55</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {helperText && (
        <FormHelperText sx={{ ml: 1.75, mt: 0.5 }}>{helperText}</FormHelperText>
      )}
    </Box>
  );
};

export default TimeIntervalInput;

